import { TITLE_FONT_SIZE } from './layout.js';
import { DYMO_FONT, estimateTitleWidth, type MeasureText } from './dymo-label.js';
import {
  PERMISSION_WAIT_NOTICE_MS,
  detectDymo,
  nothingFound,
  printToDymo,
  type DymoEnvironment,
  type DymoPrintResult,
  type DymoStatus,
} from './dymo-status.js';
import type { LabelJobItem } from './pdf.js';

/**
 * The browser's side of DYMO printing: the real {@link DymoEnvironment}, and
 * one shared, observable copy of the last detection so the destination picker,
 * the printer chooser, and the Labeling page all show the same answer from one
 * probe. No React here; useDymoStatus.ts is the hook over it.
 *
 * Two things are remembered in this browser's localStorage, beside the
 * destination: the printer chosen on the settings page, and the service origin
 * that answered last (so the next check is one request, not ten).
 */

export const DYMO_PRINTER_STORAGE_KEY = 'seller.labels.dymo.printer';
export const DYMO_ORIGIN_STORAGE_KEY = 'seller.labels.dymo.origin';

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Not remembered; this visit still works.
  }
}

/**
 * The browser's permission for this page to reach loopback addresses. Chromium
 * asks the user before a page served from elsewhere may call a service on the
 * computer itself ("Local Network Access"; the permission was first named
 * `local-network-access` and later split, loopback becoming `loopback-network`).
 * A browser that knows neither name throws, and the answer is then "cannot say".
 */
async function loopbackPermission(): Promise<'granted' | 'denied' | 'prompt' | null> {
  if (typeof navigator === 'undefined' || !navigator.permissions) return null;
  for (const name of ['loopback-network', 'local-network-access']) {
    try {
      const status = await navigator.permissions.query({ name } as unknown as PermissionDescriptor);
      return status.state;
    } catch {
      // This browser does not know the name; try the next.
    }
  }
  return null;
}

let measureContext: CanvasRenderingContext2D | null | undefined;

/** The title's width in points, measured with this browser's own copy of the label font. */
const measureTitle: MeasureText = (text) => {
  if (measureContext === undefined) {
    try {
      measureContext = document.createElement('canvas').getContext('2d');
    } catch {
      measureContext = null;
    }
  }
  if (!measureContext) return estimateTitleWidth(text);
  // A CSS pixel font size measures in the same unit, so the number is points.
  measureContext.font = `${TITLE_FONT_SIZE}px ${DYMO_FONT}, Helvetica, sans-serif`;
  return measureContext.measureText(text).width;
};

function browserEnvironment(): DymoEnvironment {
  return {
    fetch: (input, init) => fetch(input, init),
    getRememberedOrigin: () => readStored(DYMO_ORIGIN_STORAGE_KEY),
    setRememberedOrigin: (origin) => writeStored(DYMO_ORIGIN_STORAGE_KEY, origin),
    getChosenPrinter: () => readStored(DYMO_PRINTER_STORAGE_KEY),
    loopbackPermission,
    measureTitle,
  };
}

// ---------------------------------------------------------------------------
// The shared status
// ---------------------------------------------------------------------------

/** The last detection, or null before the first one finishes. */
export interface DymoSnapshot {
  status: DymoStatus | null;
  /** True while a check is running. */
  checking: boolean;
  /**
   * True once a running check has sat behind the browser's permission question
   * for {@link PERMISSION_WAIT_NOTICE_MS}: the browser is waiting for the user to
   * allow the connection. The check carries on; the page stops waiting for it.
   */
  waitingForPermission: boolean;
}

let snapshot: DymoSnapshot = { status: null, checking: false, waitingForPermission: false };
let inFlight: Promise<DymoStatus> | null = null;
const listeners = new Set<() => void>();

function publish(next: DymoSnapshot): void {
  snapshot = next;
  for (const listener of listeners) listener();
}

export function getDymoSnapshot(): DymoSnapshot {
  return snapshot;
}

export function subscribeDymo(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Checks for DYMO Connect and its printers now. Calls made while a check is
 * running share it, so several components mounting at once make one probe.
 *
 * The promise never rejects and a check always ends: a detection that throws
 * (it should not, but one that did would otherwise leave `checking` true and
 * every later call waiting on it forever) is published as "not found", so the
 * page falls back and "Check again" starts a fresh one.
 *
 * `detect` is a parameter for the tests; the pages call this with none.
 */
export function refreshDymoStatus(
  detect: (env: DymoEnvironment) => Promise<DymoStatus> = detectDymo,
): Promise<DymoStatus> {
  if (inFlight) return inFlight;
  publish({ status: snapshot.status, checking: true, waitingForPermission: false });

  let noticeTimer: ReturnType<typeof setTimeout> | undefined;
  const settle = (status: DymoStatus): DymoStatus => {
    clearTimeout(noticeTimer);
    inFlight = null;
    publish({ status, checking: false, waitingForPermission: false });
    return status;
  };
  const environment: DymoEnvironment = {
    ...browserEnvironment(),
    // The browser may now be asking its question. Give the user a moment to
    // answer it, then say what the check is waiting for.
    onPermissionPrompt: () => {
      clearTimeout(noticeTimer);
      noticeTimer = setTimeout(() => {
        if (inFlight === run) publish({ ...snapshot, waitingForPermission: true });
      }, PERMISSION_WAIT_NOTICE_MS);
    },
  };

  let started: Promise<DymoStatus>;
  try {
    started = detect(environment);
  } catch (error) {
    started = Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
  const run: Promise<DymoStatus> = started.then(settle, () => settle(nothingFound('no_service')));
  inFlight = run;
  return run;
}

/** Remembers which LabelWriter this browser prints to, and re-reads the status. */
export function chooseDymoPrinter(name: string): Promise<DymoStatus> {
  writeStored(DYMO_PRINTER_STORAGE_KEY, name);
  return refreshDymoStatus();
}

/** Prints to the DYMO printer, then refreshes the shared status (the roll count moved). */
export async function printToDymoFromBrowser(
  items: readonly LabelJobItem[],
): Promise<DymoPrintResult> {
  try {
    return await printToDymo(items, browserEnvironment());
  } finally {
    void refreshDymoStatus();
  }
}
